import { Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { APPLICATION_TYPE, FeatureFlagPipe } from '@core';
import { ChildRelationDevices, DeviceAttributeType } from '@devices';
import { IncidentsTableComponent, IncidentsTableDesktopFiltersComponent } from '@incidents';
import { Store } from '@ngrx/store';
import { IncidentsSelectors } from '@ngrx-store';
import { IncidentsFilterService, IncidentsService } from '@services';
import { NotificationsService } from '@ui';
import { Subscription } from 'rxjs';

import { MeasurementUnitDisplayPipe } from '../../pipes';
import { DeviceAttributesComponent } from '../device-attributes';
import { DeviceStatsChartComponent } from '../device-stats-chart/device-stats-chart.component';
import { SwitchPduChildrenTableComponent } from '../switch-pdu-children-table';

@Component({
  imports: [
    MatCard,
    MatCardContent,
    MatCardTitle,
    SwitchPduChildrenTableComponent,
    IncidentsTableComponent,
    IncidentsTableDesktopFiltersComponent,
    DeviceStatsChartComponent,
    SwitchPduChildrenTableComponent,
    MeasurementUnitDisplayPipe,
    FeatureFlagPipe,
    DeviceAttributesComponent,
  ],
  selector: 'avs-switch-pdu-child-details',
  styles: [`
    .port-details-units {
      color: var(--gray-700);
    }
  `],
  templateUrl: './switch-pdu-child-details.component.html',
})
export class SwitchPduChildDetailsComponent {
  port = input.required<ChildRelationDevices>();
  locationId = input.required<string>();
  parentId = input.required<string>();
  isNetworkSwitch = input.required<boolean>();

  protected isMobile = inject(APPLICATION_TYPE) === 'mobile';
  protected resolvedIncidentsLoading = false;
  protected incidentsSubs$: Subscription | undefined;
  protected realtimeAttributes = computed(() => this.port().childDevice.additionalAttributes
    .filter(attr => attr.attributeType === DeviceAttributeType.REALTIME),
  );

  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private incidentsFilterService = inject(IncidentsFilterService);
  private incidentsService = inject(IncidentsService);
  private notificationService = inject(NotificationsService);

  constructor() {
    effect(() => {
      this.refreshPortIncidents();
    });
  }

  private refreshPortIncidents() {
    this.store
      .select(IncidentsSelectors.selectIncidentsByDeviceId(this.port().childDevice.id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(incidents => {
        this.incidentsFilterService.initFilterTableType({ displayedColumns: ['status', 'reported'] });
        this.incidentsFilterService.initOpenIncidents(incidents);
      });
    this.incidentsFilterService.resolvedDates$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.getResolvedIncidents(value));
    this.incidentsFilterService.initFilterTableType({
      displayedColumns: ['status', 'reported'],
    });

  }

  private getResolvedIncidents(dateRange: { endDateTime: string; startDateTime: string; }) {
    if (dateRange.startDateTime && dateRange.startDateTime && this.port().id) {
      const params: { [key: string]: string; } = { ...dateRange, deviceId: this.port().id };

      this.resolvedIncidentsLoading = true;
      this.incidentsSubs$ = this.incidentsService
        .getAllResolvedIncidents(params)
        .subscribe({
          error: () => {
            this.resolvedIncidentsLoading = false;
            this.notificationService.showErrorMessage('Error. Try later');
          },
          next: data => {
            this.incidentsFilterService.initResolvedIncidents(data);
            this.resolvedIncidentsLoading = false;
          },
        });
    }
  }
}
